import React, { useState, useEffect, useRef } from "react";
import { db } from "../../firebase";
import { collection, getDocs, getDoc, doc, query, where, onSnapshot } from "firebase/firestore";
import { GoogleMap, useJsApiLoader, Marker, Polygon, InfoWindow } from "@react-google-maps/api";
import { MapPin, AlertTriangle, CheckCircle, Map as MapIcon, Truck, Phone, Clock } from "lucide-react";
import Modal from "../../components/Modal";
import { GOOGLE_MAPS_KEY, GOOGLE_MAPS_LIBRARIES } from "../../utils/googleMapsConfig";
import { getMapCenter } from "../../utils/timezoneCoords";
import { useLanguage } from "../../context/LanguageContext";

function CompetitorMap({ companyId, focusClient, onFocusDone }) {
  const { t } = useLanguage();
  const [clients,      setClients]      = useState([]);
  const [sections,     setSections]     = useState([]);
  const [vendors,      setVendors]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");
  const [mapCenter,    setMapCenter]    = useState({ lat: 25.2048, lng: 55.2708 });
  const [mapZoom,      setMapZoom]      = useState(11);
  const [activeLayer,  setActiveLayer]  = useState("pins");
  const [showSections, setShowSections] = useState(true);
  const [showVendors,  setShowVendors]  = useState(true);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedVendor,  setSelectedVendor]  = useState(null);
  const [selectedClient,  setSelectedClient]  = useState(null);
  const [vendorSearch,      setVendorSearch]      = useState("");
  const [revealedClientIds, setRevealedClientIds] = useState(new Set()); // client IDs revealed by clicking their zone
  const mapRef     = useRef(null);
  const heatmapRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  useEffect(() => {
    if (!companyId) return;
    loadStaticData();
    // Real-time listener for all clients — fires once on mount, then only on changes
    const unsubClients = onSnapshot(
      collection(db, "companies", companyId, "clients"),
      (snap) => {
        const clientList = snap.docs
          .map(d => {
            const data = d.data();
            const lat = data.location?.lat ?? data.lat ?? null;
            const lng = data.location?.lng ?? data.lng ?? null;
            return { id: d.id, ...data, lat, lng };
          })
          .filter(c =>
            c.lat !== null &&
            c.lng !== null &&
            !isNaN(parseFloat(c.lat)) &&
            !isNaN(parseFloat(c.lng))
          );
        setClients(clientList);
        setLoading(false);
      },
      (err) => {
        setError(`Failed to load clients: ${err.message}`);
        setLoading(false);
      }
    );
    return () => unsubClients();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Real-time vendor position listener — updates map as GPS packets arrive
  useEffect(() => {
    if (!companyId) return;
    const q = query(
      collection(db, "companies", companyId, "users"),
      where("role", "==", "vendor"),
      where("active", "==", true)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      setVendors(snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          lat:   data.last_lat   || data.lat   || null,
          lng:   data.last_lng   || data.lng   || null,
          speed: data.last_speed || data.speed || 0,
        };
      }));
    }, (err) => {
      console.error("[MAP] Vendor listener error:", err.message);
    });
    return () => unsubscribe();
  }, [companyId]);

  // When a client is passed from ClientsManager "Show on Map" button —
  // switch to pins layer, pan to that client, and open its InfoWindow
  useEffect(() => {
    if (!focusClient) return;
    const lat = parseFloat(focusClient.location?.lat ?? focusClient.lat ?? 0);
    const lng = parseFloat(focusClient.location?.lng ?? focusClient.lng ?? 0);
    if (!lat || !lng) return;
    setActiveLayer("pins");
    setMapCenter({ lat, lng });
    setMapZoom(16);
    setSelectedClient({ ...focusClient, lat, lng });
    setSelectedVendor(null);
    if (onFocusDone) onFocusDone();
  }, [focusClient]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadStaticData() {
    try {
      // Load company timezone for map center
      const configSnap = await getDoc(doc(db, "companies", companyId, "config", "settings"));
      const timezone   = configSnap.exists() ? (configSnap.data().timezone || "Asia/Dubai") : "Asia/Dubai";
      const center     = getMapCenter(timezone);
      setMapCenter({ lat: center.lat, lng: center.lng });
      setMapZoom(center.zoom || 11);

      // Load sections/zones (rarely change — one-time fetch is fine)
      const sectionsSnap = await getDocs(
        collection(db, "companies", companyId, "sections")
      );
      setSections(sectionsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError(`Failed to load map data: ${err.message} (${err.code})`);
    }
  }

  // Coverage stats
  const clientsWithGPS  = clients.length;
  const clientsWithZone = clients.filter(c => c.section).length;
  const clientsNoZone   = clients.filter(c => !c.section).length;
  const visitedClients  = clients.filter(c =>
    c.last_status === "arrived" || c.last_status === "done"
  ).length;
  const coveragePercent = clientsWithGPS > 0
    ? Math.round((visitedClients / clientsWithGPS) * 100)
    : 0;

  // Build id → name lookup for sections
  const sectionNameById = {};
  sections.forEach(s => { sectionNameById[s.id] = s.name; });

  // Group clients by section name for sidebar stats
  const clientsBySection = {};
  clients.forEach(c => {
    const name = (c.section && sectionNameById[c.section]) || (c.section ? c.section : "Unassigned");
    clientsBySection[name] = (clientsBySection[name] || 0) + 1;
  });
  const topSections = Object.entries(clientsBySection)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  function handleZoneClick(section, e) {
    if (!section.polygon || section.polygon.length < 3) return;
    const gmPolygon = new window.google.maps.Polygon({
      paths: section.polygon.map(p => ({ lat: p.lat, lng: p.lng })),
    });
    let total = 0, visited = 0;
    const newIds = new Set();
    for (const client of clients) {
      const lat = parseFloat(client.lat);
      const lng = parseFloat(client.lng);
      if (isNaN(lat) || isNaN(lng)) continue;
      const inside = window.google.maps.geometry.poly.containsLocation(
        new window.google.maps.LatLng(lat, lng), gmPolygon
      );
      if (inside) {
        total++;
        newIds.add(client.id);
        if (client.last_status === "arrived" || client.last_status === "done") visited++;
      }
    }
    setSelectedSection({ section, total, visited, remaining: total - visited });
    // Reveal the client pins found inside — accumulates across clicks, resets only on section navigate
    setRevealedClientIds(prev => new Set([...prev, ...newIds]));
  }

  function focusVendor(vendor) {
    if (vendor.lat && vendor.lng) {
      setMapCenter({ lat: parseFloat(vendor.lat), lng: parseFloat(vendor.lng) });
      setMapZoom(15);
    }
  }

  function getVendorStatus(vendor) {
    if (vendor.last_seen) {
      const d    = vendor.last_seen.toDate ? vendor.last_seen.toDate() : new Date(vendor.last_seen);
      const mins = (Date.now() - d.getTime()) / 60000;
      if (mins < 5)  return "online";
      if (mins < 60) return "idle";
      return "offline";
    }
    return vendor.is_online ? "online" : "offline";
  }

  function formatLastSeen(lastSeen) {
    if (!lastSeen) return null;
    const d    = lastSeen.toDate ? lastSeen.toDate() : new Date(lastSeen);
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    if (mins < 1)  return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)  return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  function formatVisitDate(ts) {
    if (!ts) return null;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  }

  // Build vendorId → name lookup for client last-visited-by display
  const vendorNameById = {};
  vendors.forEach(v => {
    vendorNameById[v.id] = `${v.first_name || ""} ${v.last_name || ""}`.trim();
  });

  function onMapLoad(map) {
    mapRef.current = map;
    updateHeatmap(map);
  }

  function updateHeatmap(map) {
    if (!window.google?.maps?.visualization) return;

    // Clear existing heatmap
    if (heatmapRef.current) {
      heatmapRef.current.setMap(null);
    }

    if (activeLayer !== "heatmap" || clients.length === 0) return;

    const heatmapData = clients
      .filter(c => c.lat && c.lng)
      .map(c => new window.google.maps.LatLng(
        parseFloat(c.lat),
        parseFloat(c.lng)
      ));

    heatmapRef.current = new window.google.maps.visualization.HeatmapLayer({
      data: heatmapData,
      map: map,
      radius: 20,
      opacity: 0.7,
    });
  }

  // Re-render heatmap when clients finish loading or layer changes
  useEffect(() => {
    if (mapRef.current && clients.length > 0) {
      updateHeatmap(mapRef.current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, activeLayer]);

  if (!isLoaded) return (
    <div className="bg-white rounded-xl shadow-sm p-12 text-center">
      <p className="text-gray-400 text-sm">{t("cmap_loading_map")}</p>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6">
      <p className="text-red-700 text-sm font-medium">{error}</p>
      <button onClick={loadStaticData} className="mt-3 text-sm text-red-600 underline">{t("cmap_retry")}</button>
    </div>
  );

  return (
    <div className="flex flex-col h-full space-y-4">

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-yellow-500" /> {t("cmap_title")}
          </h2>

          {/* Layer selector */}
          <div className="flex gap-1">
            {[
              { id: "heatmap",  label: t("cmap_heat_map")    },
              { id: "pins",     label: t("cmap_client_pins") },
              { id: "coverage", label: t("cmap_coverage")    },
            ].map(layer => (
              <button key={layer.id}
                onClick={() => setActiveLayer(layer.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  activeLayer === layer.id
                    ? "bg-yellow-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}>
                {layer.label}
              </button>
            ))}
          </div>

          {/* Overlay toggles */}
          <div className="flex gap-3">
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showSections}
                onChange={e => setShowSections(e.target.checked)}
                className="rounded"
              />
              {t("cmap_show_zones")}
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showVendors}
                onChange={e => setShowVendors(e.target.checked)}
                className="rounded"
              />
              {t("cmap_show_vendors")}
            </label>
          </div>

        </div>
      </div>

      {/* Coverage stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: t("cmap_clients_on_map"), value: clientsWithGPS,        color: "text-blue-600"   },
          { label: t("cmap_with_zone"),      value: clientsWithZone,       color: "text-green-600"  },
          { label: t("cmap_no_zone"),        value: clientsNoZone,         color: "text-orange-600" },
          { label: t("cmap_visited"),        value: visitedClients,        color: "text-green-600"  },
          { label: t("cmap_coverage_pct"),   value: `${coveragePercent}%`, color: coveragePercent > 70 ? "text-green-600" : coveragePercent > 40 ? "text-yellow-600" : "text-red-600" },
        ].map((card, i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm p-4 text-center">
            <p className={`text-xl font-bold ${card.color}`}>{card.value}</p>
            <p className="text-gray-500 text-xs mt-0.5">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Map + sidebar layout */}
      <div className="flex flex-col md:flex-row gap-4 flex-1" style={{ minHeight: "500px" }}>

        {/* Map */}
        <div className="flex-1 rounded-xl overflow-hidden shadow-sm relative">
          {loading ? (
            <div className="w-full bg-gray-100 flex items-center justify-center" style={{ minHeight: "400px" }}>
              <p className="text-gray-400 text-sm">{t("cmap_loading_data")}</p>
            </div>
          ) : (
            <GoogleMap
              onLoad={onMapLoad}
              center={mapCenter}
              zoom={mapZoom}
              mapContainerStyle={{ width: "100%", height: "100%" }}
              options={{
                mapTypeId: "roadmap",
                streetViewControl: false,
                mapTypeControl: true,
              }}>

              {/* Section polygons — in coverage mode: colored green/yellow/red by visit % */}
              {showSections && sections.map(section => {
                if (!section.polygon || section.polygon.length === 0) return null;
                let fillColor   = section.color || "#6366f1";
                let fillOpacity = 0.15;
                let strokeColor = section.color || "#6366f1";
                if (activeLayer === "coverage") {
                  const total   = clients.filter(c => c.section === section.id).length;
                  const visited = clients.filter(c =>
                    c.section === section.id &&
                    (c.last_status === "arrived" || c.last_status === "done")
                  ).length;
                  const pct = total > 0 ? Math.round((visited / total) * 100) : 0;
                  fillColor   = pct > 70 ? "#22c55e" : pct > 40 ? "#eab308" : "#ef4444";
                  strokeColor = fillColor;
                  fillOpacity = 0.35;
                }
                return (
                  <Polygon
                    key={section.id}
                    paths={section.polygon.map(p => ({ lat: p.lat, lng: p.lng }))}
                    options={{ fillColor, fillOpacity, strokeColor, strokeWeight: 2, clickable: true }}
                    onClick={e => handleZoneClick(section, e)}
                  />
                );
              })}

              {/* Client pins — shown in Client Pins and Coverage layers.
                  - No section assigned → always visible (genuinely unzoned)
                  - Has section → hidden until that zone polygon is clicked
                  - revealedClientIds is populated by geometry in handleZoneClick */}
              {(activeLayer === "pins" || activeLayer === "coverage") && clients
                .filter(client => !client.section || revealedClientIds.has(client.id))
                .map(client => (
                  <Marker
                    key={client.id}
                    position={{ lat: parseFloat(client.lat), lng: parseFloat(client.lng) }}
                    icon={{
                      url: client.last_status === "arrived" || client.last_status === "done"
                        ? "/icons/green-pin.png"
                        : "/icons/red-pin.png",
                      scaledSize: new window.google.maps.Size(18, 18),
                      anchor:     new window.google.maps.Point(9, 18),
                    }}
                    title={`${client.first_name || ""} ${client.last_name || ""}`.trim()}
                    onClick={() => { setSelectedClient(client); setSelectedVendor(null); }}
                  />
                ))}

              {/* Vendor markers */}
              {showVendors && vendors.map(vendor => (
                vendor.lat && vendor.lng && (
                  <Marker
                    key={vendor.id}
                    position={{ lat: parseFloat(vendor.lat), lng: parseFloat(vendor.lng) }}
                    icon={{
                      url: (vendor.speed || 0) > 0 ? "/icons/car-green.png" : "/icons/car.png",
                      scaledSize: new window.google.maps.Size(40, 40),
                      anchor:     new window.google.maps.Point(20, 20),
                    }}
                    title={`${vendor.first_name || ""} ${vendor.last_name || ""}`.trim()}
                    onClick={() => { setSelectedVendor(vendor); setSelectedClient(null); }}
                  />
                )
              ))}

              {/* Vendor InfoWindow */}
              {selectedVendor && selectedVendor.lat && selectedVendor.lng && (
                <InfoWindow
                  position={{ lat: parseFloat(selectedVendor.lat), lng: parseFloat(selectedVendor.lng) }}
                  onCloseClick={() => setSelectedVendor(null)}>
                  <div className="p-1 min-w-[150px]">
                    <p className="font-semibold text-gray-800 text-sm">
                      {`${selectedVendor.first_name || ""} ${selectedVendor.last_name || ""}`.trim()}
                    </p>
                    {selectedVendor.plate && (
                      <p className="text-gray-500 text-xs mt-0.5 font-mono">🚗 {selectedVendor.plate}</p>
                    )}
                    {(() => {
                      const st        = getVendorStatus(selectedVendor);
                      const color     = st === "online" ? "text-green-600" : st === "idle" ? "text-yellow-600" : "text-gray-400";
                      const label     = st === "online" ? t("cmap_online") : st === "idle" ? t("cmap_idle") : t("cmap_offline");
                      const lastSeen  = formatLastSeen(selectedVendor.last_seen);
                      return (
                        <p className={`text-xs font-medium mt-1 ${color}`}>
                          ● {label}{lastSeen ? ` — ${lastSeen}` : ""}
                        </p>
                      );
                    })()}
                    <p className="text-gray-500 text-xs mt-0.5 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatLastSeen(selectedVendor.last_seen) || "No data"}
                    </p>
                    <p className="text-gray-500 text-xs mt-0.5">
                      💨 {selectedVendor.speed || 0} km/h
                    </p>
                  </div>
                </InfoWindow>
              )}

              {/* Client InfoWindow */}
              {selectedClient && (
                <InfoWindow
                  position={{ lat: parseFloat(selectedClient.lat), lng: parseFloat(selectedClient.lng) }}
                  onCloseClick={() => setSelectedClient(null)}>
                  <div className="p-1 min-w-[150px]">
                    <p className="font-semibold text-gray-800 text-sm">
                      {`${selectedClient.first_name || ""} ${selectedClient.last_name || ""}`.trim() || "—"}
                    </p>
                    {selectedClient.phone && (
                      <p className="text-gray-500 text-xs mt-0.5 flex items-center gap-1">
                        <Phone className="w-3 h-3" />{selectedClient.phone}
                      </p>
                    )}
                    <p className="text-gray-500 text-xs mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {selectedClient.last_visited_at
                        ? `${t("cmap_last_visit")} ${formatVisitDate(selectedClient.last_visited_at)}`
                        : t("cmap_never_visited")}
                    </p>
                    {selectedClient.last_visited_by && vendorNameById[selectedClient.last_visited_by] && (
                      <p className="text-gray-500 text-xs mt-0.5 flex items-center gap-1">
                        <Truck className="w-3 h-3" />
                        {vendorNameById[selectedClient.last_visited_by]}
                      </p>
                    )}
                  </div>
                </InfoWindow>
              )}

            </GoogleMap>
          )}
        </div>

        {/* Zone stats modal */}
        <Modal isOpen={!!selectedSection} onClose={() => setSelectedSection(null)}
          title={selectedSection?.section.name || "Zone"} size="sm">
          {selectedSection && (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: (selectedSection.section.color || "#6366f1") + "22",
                    border: `2px solid ${selectedSection.section.color || "#6366f1"}`,
                  }}>
                  <MapIcon className="w-5 h-5" style={{ color: selectedSection.section.color || "#6366f1" }} />
                </div>
                <div className="flex-1">
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-gray-800">{selectedSection.total}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{t("cmap_total")}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-600">{selectedSection.visited}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{t("cmap_visited")}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-red-500">{selectedSection.remaining}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{t("cmap_remaining")}</p>
                    </div>
                  </div>
                  {selectedSection.total > 0 && (
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>{t("cmap_progress")}</span>
                        <span className="font-medium">
                          {Math.round((selectedSection.visited / selectedSection.total) * 100)}%
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full transition-all"
                          style={{ width: `${Math.round((selectedSection.visited / selectedSection.total) * 100)}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex pt-2 border-t">
                <button onClick={() => setSelectedSection(null)}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium px-5 py-2 rounded-lg">
                  {t("cmap_close")}
                </button>
              </div>
            </div>
          )}
        </Modal>

        {/* Right sidebar — section stats */}
        <div className="w-full md:w-64 shrink-0 space-y-3 overflow-y-auto">

          {/* Top sections by client count */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h3 className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> {t("cmap_clients_by_zone")}
            </h3>
            {topSections.length === 0 ? (
              <p className="text-xs text-gray-400">{t("cmap_no_zones_defined")}</p>
            ) : (
              <div className="space-y-2">
                {topSections.map(([name, count], i) => (
                  <div key={i}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className="text-gray-600 truncate max-w-[130px]">{name}</span>
                      <span className="font-medium text-gray-800">{count}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div
                        className="bg-yellow-500 h-1.5 rounded-full"
                        style={{ width: `${(count / topSections[0][1]) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Vendor list */}
          {vendors.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                <Truck className="w-3.5 h-3.5" /> {t("cmap_vendors")} ({vendors.length})
              </h3>
              <input
                type="text"
                value={vendorSearch}
                onChange={e => setVendorSearch(e.target.value)}
                placeholder={t("cmap_search_vendor")}
                className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 mb-2 outline-none focus:border-yellow-400"
              />
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {vendors
                  .filter(v => {
                    const name = `${v.first_name || ""} ${v.last_name || ""}`.trim().toLowerCase();
                    return name.includes(vendorSearch.toLowerCase());
                  })
                  .sort((a, b) => {
                    const order = { online: 0, idle: 1, offline: 2 };
                    return (order[getVendorStatus(a)] ?? 2) - (order[getVendorStatus(b)] ?? 2);
                  })
                  .map(vendor => {
                    const status   = getVendorStatus(vendor);
                    const isOnline = status === "online" || status === "idle";
                    const lastSeen = formatLastSeen(vendor.last_seen);
                    return (
                      <button
                        key={vendor.id}
                        onClick={() => focusVendor(vendor)}
                        disabled={!vendor.lat && !vendor.lng}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition text-xs ${
                          vendor.lat || vendor.lng
                            ? "hover:bg-yellow-50 cursor-pointer"
                            : "opacity-40 cursor-default"
                        }`}>
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          isOnline ? "bg-green-500" : "bg-gray-400"
                        }`} />
                        <span className="truncate text-gray-700 font-medium flex-1">
                          {`${vendor.first_name || ""} ${vendor.last_name || ""}`.trim() || vendor.email}
                        </span>
                        {lastSeen && (
                          <span className={`text-[10px] shrink-0 ${isOnline ? "text-green-600" : "text-gray-400"}`}>
                            {isOnline ? "Live" : lastSeen}
                          </span>
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Coverage by zone */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h3 className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5" /> {t("cmap_visit_coverage")}
            </h3>
            <div className="space-y-2">
              {sections.slice(0, 6).map(section => {
                const total   = clients.filter(c => c.section === section.id).length;
                const visited = clients.filter(c =>
                  c.section === section.id &&
                  (c.last_status === "arrived" || c.last_status === "done")
                ).length;
                const pct = total > 0 ? Math.round((visited / total) * 100) : 0;
                return (
                  <div key={section.id}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className="text-gray-600 truncate max-w-[110px]">{section.name}</span>
                      <span className={`font-medium ${pct > 70 ? "text-green-600" : pct > 40 ? "text-yellow-600" : "text-red-600"}`}>
                        {pct}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full ${pct > 70 ? "bg-green-500" : pct > 40 ? "bg-yellow-500" : "bg-red-500"}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-400">{visited}/{total} clients</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Unassigned clients warning */}
          {clientsNoZone > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-orange-700 flex items-center gap-1.5 mb-1">
                <AlertTriangle className="w-3.5 h-3.5" /> {clientsNoZone} clients have no zone
              </p>
              <p className="text-xs text-orange-600">
                Assign them to zones for better coverage tracking and vendor routing.
              </p>
            </div>
          )}

        </div>
      </div>

    </div>
  );
}

export default CompetitorMap;
